import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HeartPulse, Activity, Pill, Cross, Syringe, Stethoscope, ShieldCheck, Leaf } from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      toast.error(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy-dark flex overflow-hidden">
      {/* ===== LEFT PANEL: Hero Image & Branding ===== */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/Stethescope.jpg')" }}
        />
        
        {/* Multi-layer Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy/80 to-navy/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 via-transparent to-navy-dark/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/20 via-transparent to-brand-blue/10" />

        {/* Decorative Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" 
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '40px 40px' }} 
        />

        {/* Animated Floating Medical Icons */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Heartbeat icon */}
          <div className="absolute top-[15%] left-[12%] animate-float-1">
            <div className="w-14 h-14 glass rounded-2xl flex items-center justify-center">
              <HeartPulse className="h-7 w-7 text-green-400 animate-heartbeat" />
            </div>
          </div>
          {/* Cross/Medical icon */}
          <div className="absolute top-[25%] right-[15%] animate-float-2 anim-delay-1">
            <div className="w-12 h-12 glass rounded-xl flex items-center justify-center">
              <svg className="h-6 w-6 text-brand-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M2 12h20" />
              </svg>
            </div>
          </div>
          {/* Pill icon */}
          <div className="absolute bottom-[35%] left-[20%] animate-float-3 anim-delay-2">
            <div className="w-12 h-12 glass rounded-xl flex items-center justify-center">
              <Pill className="h-6 w-6 text-purple-400" />
            </div>
          </div>
          {/* Activity icon */}
          <div className="absolute bottom-[20%] right-[10%] animate-float-1 anim-delay-3">
            <div className="w-10 h-10 glass rounded-xl flex items-center justify-center">
              <Activity className="h-5 w-5 text-orange-400" />
            </div>
          </div>
          {/* Shield icon */}
          <div className="absolute top-[60%] left-[8%] animate-float-2 anim-delay-1">
            <div className="w-10 h-10 glass rounded-xl flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-green-400" />
            </div>
          </div>
          {/* Syringe icon */}
          <div className="absolute top-[40%] right-[8%] animate-float-3 anim-delay-2">
            <div className="w-10 h-10 glass rounded-xl flex items-center justify-center">
              <Syringe className="h-5 w-5 text-blue-400" />
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo & Brand */}
          <div className="animate-slide-in-left">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/15 glass rounded-xl flex items-center justify-center">
                <Leaf className="h-7 w-7 text-green-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-xl tracking-wide">Nutriaxis</h2>
                <p className="text-white/60 text-xs tracking-widest uppercase">Wellness Clinic</p>
              </div>
            </div>
          </div>

          {/* Center Quote / Tagline */}
          <div className="animate-fade-in-up">
            <div className="max-w-md">
              <div className="inline-block px-4 py-1.5 glass rounded-full mb-6">
                <span className="text-white/70 text-xs tracking-widest uppercase flex items-center gap-2">
                  <Stethoscope className="h-3.5 w-3.5 text-green-400" />
                  Healthcare Management System
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Your Wellness{' '}
                <span className="gradient-text">Journey</span>
                <br />
                Starts Here
              </h1>
              <p className="text-white/60 text-lg leading-relaxed">
                Empowering healthcare professionals with seamless patient management,
                scheduling, and wellness tracking — all in one place.
              </p>
              
              {/* Feature Pills */}
              <div className="flex flex-wrap gap-3 mt-8">
                {['Patient Management', 'Appointment Scheduling', 'Wellness Tracking'].map((feature, i) => (
                  <span
                    key={feature}
                    className="px-4 py-1.5 glass rounded-full text-white/70 text-sm animate-fade-in-up"
                    style={{ animationDelay: `${0.3 + i * 0.15}s` }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Info */}
          <div className="animate-fade-in-up">
            <p className="text-white/40 text-sm flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-400" />
              Secure &amp; HIPAA-Compliant Platform
            </p>
          </div>
        </div>
      </div>

      {/* ===== RIGHT PANEL: Login Form ===== */}
      <div className="w-full lg:w-[45%] min-h-screen flex items-center justify-center relative bg-gradient-to-br from-navy-dark via-navy to-navy px-4 sm:px-8">
        {/* Background decorative blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-blue/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-green/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-brand-purple/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>

        {/* Mobile Logo (visible only on small screens) */}
        <div className="lg:hidden absolute top-8 left-8 flex items-center gap-3 animate-fade-in-up">
          <div className="w-10 h-10 bg-white/10 glass rounded-xl flex items-center justify-center">
            <Leaf className="h-6 w-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Nutriaxis</h2>
            <p className="text-white/50 text-[10px] tracking-widest uppercase">Wellness Clinic</p>
          </div>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-md relative z-10 animate-slide-in-right">
          {/* Card Header */}
          <div className="text-center mb-8">
            {/* Avatar/Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-green to-brand-green-700 rounded-2xl flex items-center justify-center shadow-xl shadow-brand-green/20 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                  <Stethoscope className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full border-2 border-navy-dark flex items-center justify-center animate-pulse-glow">
                  <ShieldCheck className="h-3 w-3 text-navy-dark" />
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white">Welcome Back</h1>
            <p className="text-white/50 mt-1">Sign in to access your dashboard</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="animate-fade-in-up anim-delay-1">
              <label className="block text-sm font-medium text-white/70 mb-2">
                Username
              </label>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-brand-green/20 to-brand-blue/20 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-xl" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="relative w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green/50 focus:border-brand-green/50 text-white placeholder-white/30 transition-all duration-300"
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div className="animate-fade-in-up anim-delay-2">
              <label className="block text-sm font-medium text-white/70 mb-2">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-brand-green/20 to-brand-blue/20 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-xl" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="relative w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green/50 focus:border-brand-green/50 text-white placeholder-white/30 transition-all duration-300"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between animate-fade-in-up anim-delay-3">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-white/10 bg-white/5 text-brand-green focus:ring-brand-green/50 cursor-pointer"
                />
                <span className="text-sm text-white/50 group-hover:text-white/70 transition-colors">Remember me</span>
              </label>
              <button
                type="button"
                className="text-sm text-brand-green hover:text-green-400 transition-colors"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="relative w-full py-3 rounded-xl font-medium text-white overflow-hidden group animate-fade-in-up anim-delay-4"
            >
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-green to-brand-green-600 group-hover:from-brand-green-600 group-hover:to-brand-green-700 transition-all duration-300" />
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
                  transform: 'skewX(-20deg)',
                  animation: 'none',
                }}
              />
              <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Logging in...
                  </>
                ) : (
                  <>
                    Sign In
                    <svg className="h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8 animate-fade-in-up">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/5"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-xs text-white/30 bg-navy-dark">Secure Login</span>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center animate-fade-in-up">
            <p className="text-white/30 text-sm flex items-center justify-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-green-400/50" />
              Nutriaxis Wellness Clinic &copy; {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

